import {useCallback, useContext, useMemo} from 'react';
import _ from 'underscore';
import {InternalContext} from '../../store/context';
import useWhenChangedEffect from '@netx/core-components/hooks/useWhenChangedEffect';
export interface UseArrowFocusTrapItemArguments {
	index: number;
	ref?: React.RefObject<HTMLElement>;
}

export const useArrowFocusTrapItem = ({
	index,
	ref,
}: UseArrowFocusTrapItemArguments) => {
	const {
		activeIndex,
		disabled,
		userDisabled,
		handleItemClick,
		handleItemInteractStart,
		setActiveIndex,
		justClickedItem,
	} = useContext(InternalContext);

	const isActive = !disabled && !userDisabled && activeIndex === index;
	const tabIndex = isActive ? 0 : -1;

	useWhenChangedEffect(
		isActive,
		useCallback(
			_.debounce((isActive) => {
				if (
					!ref?.current ||
					!isActive ||
					justClickedItem?.current ||
					ref.current.contains(document.activeElement)
				)
					return;
				ref.current.focus({preventScroll: true});
			}, 100),
			[],
		),
		{
			executeAtMount: true,
		},
	);

	const setActive = useCallback(() => setActiveIndex(index), [index]);

	const memoizedAttributes = useMemo(
		() => ({
			tabIndex,
		}),
		[tabIndex],
	);

	return {
		attributes: memoizedAttributes,
		handleItemClick: handleItemClick(index),
		handleItemInteractStart: handleItemInteractStart(index),
		setActive,
		isActive,
		justClickedItem,
	};
};

export default useArrowFocusTrapItem;
